import { Directive, ElementRef, NgZone, OnDestroy, OnInit, Renderer2, RendererStyleFlags2 } from '@angular/core';

/** Remembered per device — how much room the rail gets depends on the screen, like the bubble position. */
const STORAGE_KEY = 'trendmulti.chat.rail.widthPx';
/** Photos only: the header avatar (2.25rem) plus the header padding. */
const COLLAPSED_PX = 56;
/** Narrowest rail that still fits the names and the header actions. */
const MIN_PX = 240;
/** The 20rem the rail had before it could be resized. */
const DEFAULT_PX = 320;
const MAX_PX = 480;
/** A rail dragged below halfway to the minimum snaps to photos only. */
const COLLAPSE_BELOW_PX = (COLLAPSED_PX + MIN_PX) / 2;
/** Arrow-key step — the same as the bubble's (`draggable-fab`). */
const KEYBOARD_STEP_PX = 16;
/** Side by side only: under the phone breakpoint the rail is the whole screen. */
const DESKTOP_QUERY = '(min-width: 768px)';

/** One press-and-drag on the handle, from `pointerdown` to release. */
interface RailDrag {
  pointerId: number;
  originClientX: number;
  originWidthPx: number;
  /** The pointer travelled: the `click`/`dblclick` this press ends with meant "resize". */
  moved: boolean;
}

/**
 * Resizable rail of the chat app (DEV-2062) — applied to the rail itself.
 *
 * One writer, like `draggable-fab`: the width is the `--chat-rail-width` custom
 * property painted on the host, which the shell stylesheet reads. Dragged below
 * `COLLAPSE_BELOW_PX` the rail snaps to the photos-only width and reports
 * `collapsed`, so the shell and the conversation list drop what needs room.
 *
 * The handle in the shell template drives it: a pointer drag (the move listener
 * runs outside the zone — only a collapse flip needs change detection), the arrow
 * keys, and Enter or a double click to collapse and restore (the WAI-ARIA window
 * splitter pattern).
 */
@Directive({
  selector: '[chatAppRailResize]',
  exportAs: 'chatAppRailResize',
  standalone: false,
})
export class ChatAppRailResizeDirective implements OnInit, OnDestroy {

  readonly minPx = COLLAPSED_PX;

  readonly maxPx = MAX_PX;

  widthPx = DEFAULT_PX;

  /** Photos only. Never under the phone breakpoint, where the rail is full width. */
  collapsed = false;

  /** Where Enter or a double click takes a collapsed rail back to. */
  private expandedPx = DEFAULT_PX;

  private drag: RailDrag | null = null;

  /** Whether the last press was a drag — two quick drags still fire `dblclick`. */
  private lastDragMoved = false;

  /** Tears down the out-of-zone `pointermove` listener of the running drag. */
  private stopMoveListening: (() => void) | null = null;

  private readonly desktop = window.matchMedia(DESKTOP_QUERY);

  /** Crossing the breakpoint re-evaluates `collapsed` (the listener runs in the zone). */
  private readonly onBreakpointChange = (): void => this.paint(this.widthPx);

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly ngZone: NgZone,
  ) { }

  ngOnInit(): void {
    // Resolved against THIS viewport: a width saved on a wider screen must not
    // take more than half of this one.
    this.paint(this.resolveWidth(this.restore()));
    if (this.widthPx !== COLLAPSED_PX) this.expandedPx = this.widthPx;
    this.desktop.addEventListener('change', this.onBreakpointChange);
  }

  ngOnDestroy(): void {
    this.desktop.removeEventListener('change', this.onBreakpointChange);
    this.endDrag();
  }

  // ---------------------------------------------------------------------------
  // Handle
  // ---------------------------------------------------------------------------

  /** The pointer is captured on the handle, so the drag keeps tracking wherever it goes. */
  onPointerDown(event: PointerEvent): void {
    if (event.button !== 0 || !event.isPrimary) return;
    // No text selection under a drag.
    event.preventDefault();
    this.endDrag();
    const handle = event.currentTarget as HTMLElement;
    this.drag = { pointerId: event.pointerId, originClientX: event.clientX, originWidthPx: this.widthPx, moved: false };
    try {
      handle.setPointerCapture(event.pointerId);
    } catch {
      // Inactive pointer id — the drag still works while the pointer stays on the handle.
    }
    // Keyboard continuity after a pointer drag (WebKit skips the focus change once
    // `pointerdown` is cancelled).
    handle.focus();
    this.renderer.addClass(this.host.nativeElement, 'is-resizing');
    this.stopMoveListening = this.ngZone.runOutsideAngular(() =>
      this.renderer.listen(handle, 'pointermove', (move: PointerEvent) => this.onPointerMove(move)),
    );
  }

  /** `pointerup`, `pointercancel` and `lostpointercapture` all land here; the first ends the drag. */
  onPointerRelease(): void {
    this.endDrag();
  }

  onKeydown(event: KeyboardEvent): void {
    const isCollapsed = this.widthPx === COLLAPSED_PX;
    switch (event.key) {
      case 'ArrowLeft':
        // Never collapses: one step below the minimum still resolves to the minimum.
        if (!isCollapsed) this.apply(this.widthPx - KEYBOARD_STEP_PX);
        break;
      case 'ArrowRight':
        this.apply(isCollapsed ? this.expandedPx : this.widthPx + KEYBOARD_STEP_PX);
        break;
      case 'Enter':
        this.toggleCollapsed();
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  toggleCollapsed(): void {
    this.apply(this.widthPx === COLLAPSED_PX ? this.expandedPx : COLLAPSED_PX);
  }

  /** A double click on the handle collapses/restores — unless it was two quick drags. */
  onDoubleClick(): void {
    if (this.lastDragMoved) return;
    this.toggleCollapsed();
  }

  // ---------------------------------------------------------------------------
  // Mechanics
  // ---------------------------------------------------------------------------

  private onPointerMove(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (event.clientX !== drag.originClientX) drag.moved = true;
    const next = this.resolveWidth(drag.originWidthPx + event.clientX - drag.originClientX);
    if (next === this.widthPx) return;
    // The width itself is plain CSS; only a collapse flip changes the templates.
    if ((next === COLLAPSED_PX) !== (this.widthPx === COLLAPSED_PX)) {
      this.ngZone.run(() => this.paint(next));
    } else {
      this.paint(next);
    }
  }

  private endDrag(): void {
    const drag = this.drag;
    if (!drag) return;
    this.lastDragMoved = drag.moved;
    this.drag = null;
    this.stopMoveListening?.();
    this.stopMoveListening = null;
    this.renderer.removeClass(this.host.nativeElement, 'is-resizing');
    this.remember();
  }

  /** Photos only below the threshold; otherwise within the expanded range. */
  private resolveWidth(px: number): number {
    if (px < COLLAPSE_BELOW_PX) return COLLAPSED_PX;
    return Math.min(this.maxForViewport(), Math.max(MIN_PX, Math.round(px)));
  }

  /** Never more than half the window — the pane is what the rail makes room for. */
  private maxForViewport(): number {
    return Math.max(MIN_PX, Math.min(MAX_PX, Math.floor(window.innerWidth / 2)));
  }

  private apply(px: number): void {
    this.paint(this.resolveWidth(px));
    this.remember();
  }

  /** The single writer of the rail width. */
  private paint(px: number): void {
    this.widthPx = px;
    this.collapsed = px === COLLAPSED_PX && this.desktop.matches;
    this.renderer.setStyle(this.host.nativeElement, '--chat-rail-width', `${px}px`, RendererStyleFlags2.DashCase);
  }

  private remember(): void {
    if (this.widthPx !== COLLAPSED_PX) this.expandedPx = this.widthPx;
    try {
      localStorage.setItem(STORAGE_KEY, String(this.widthPx));
    } catch {
      // Storage unavailable (private mode) — the width lasts the session.
    }
  }

  /** The raw remembered width — `resolveWidth` decides what it means on this viewport. */
  private restore(): number {
    try {
      const stored = Number(localStorage.getItem(STORAGE_KEY));
      return Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_PX;
    } catch {
      return DEFAULT_PX;
    }
  }
}
